import { TFunction } from 'i18next';

import { MegaMenuBlockTypes, MegaMenuLeftBlockItemsTypes } from '~/blocks/Header/types';
import { ContentAvailability } from '~/hooks/useContentAvailability';
import {
  ANALYTICS_ROUTE,
  buildDiscoverRoute,
  DISCOVER_COURSES_ROUTE,
  DISCOVER_GLOSSARY_ROUTE,
  DISCOVER_INSIGHTS_ROUTE,
  DISCOVER_PATHWAYS_ROUTE,
  DISCOVER_SEARCH_ROUTE,
  DISCOVER_VIDEOS_ROUTE,
  DiscoverRoutes,
  EDIT_ACCOUNT,
  LOGIN_ROUTE,
  MEMBERSHIP_AREA,
  MY_ACCOUNT,
} from '~/routes';
import {
  Maybe,
  MegaMenuLeftBlock,
  MegaMenuMiddleBlock,
  MegaMenuRightBlock,
  NavigationMegaMenuBlock,
  Video,
  VideoCategory,
} from '~/types';
import { checkIsIframed } from '~/utils/checkIsIframed';
import { toBoolean } from '~/utils/toBoolean';

export const PROFILE_LINKS = [
  {
    title: 'header.myAccount',
    url: MY_ACCOUNT,
  },
  {
    title: 'header.myMembership',
    url: MEMBERSHIP_AREA,
  },
  {
    title: 'header.myWatchlist',
    url: `${ANALYTICS_ROUTE}#watchlist`,
  },
];
export const PROFILE_LINKS_SSKU = [
  {
    title: 'header.myAccount',
    url: EDIT_ACCOUNT,
  },
  {
    title: 'header.myWatchlist',
    url: `${ANALYTICS_ROUTE}#watchlist`,
  },
];

export type ExpandedNavigationItem = {
  title: string;
  url: string;
  type: 'link' | 'MegaMenu';
  className?: string;
  managerUrl?: string;
  avoidManagerUrlWithoutSub?: boolean;
  blocks?: NavigationMegaMenuBlock[];
};

type MiddleBlockItems = {
  title: string;
  sub_title: string;
  link: string;
  image_url: string;
};

export const createMiddleItems = (contentAvailability: ContentAvailability) =>
  [
    contentAvailability.pathways && {
      title: 'shortCourses',
      sub_title: 'shortCoursesDescription',
      link: DISCOVER_PATHWAYS_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/pathwaysIcon.svg',
    },
    contentAvailability.courses && {
      title: 'certificates',
      sub_title: 'certificatesDescription',
      link: DiscoverRoutes.Courses,
      image_url: '/headers-images/MegaMenuIcons/coursesIcon.svg',
    },
    {
      title: 'videosModules',
      sub_title: 'videoModulesDescription',
      link: DISCOVER_VIDEOS_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/videosIcon.svg',
    },
    contentAvailability.glossary && {
      title: 'glossary',
      sub_title: 'glossaryDescription',
      link: DISCOVER_GLOSSARY_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/glossaryIcon.svg',
    },
    contentAvailability.insights && {
      title: 'insights',
      sub_title: 'insightsDescription',
      link: DISCOVER_INSIGHTS_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/insightsIcon.svg',
    },
  ].filter(toBoolean<MiddleBlockItems>);

export const createMiddleItemsSsku = (contentAvailability: ContentAvailability) =>
  [
    contentAvailability.courses && {
      title: 'certificates',
      sub_title: 'certificatesDescription',
      link: DISCOVER_COURSES_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/trackIcon.svg',
    },
    contentAvailability.pathways && {
      title: 'shortCourses',
      sub_title: 'shortCoursesDescription',
      link: DISCOVER_PATHWAYS_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/sprintIcon_updated.svg',
    },
    {
      title: 'videosModules',
      sub_title: 'videoModulesDescription',
      link: DISCOVER_VIDEOS_ROUTE,
      image_url: '/headers-images/MegaMenuIcons/stepIcon.svg',
    },
  ].filter(toBoolean<MiddleBlockItems>);

export const createMegaMenuBlocks = ({
  t,
  contentAvailability,
  baseCategoriesList,
  twoLatestVideos,
  isSsku,
}: {
  t: TFunction;
  contentAvailability: ContentAvailability;
  baseCategoriesList: VideoCategory[];
  twoLatestVideos: Video[];
  isSsku: boolean;
}): NavigationMegaMenuBlock[] => {
  const leftBlock: Maybe<MegaMenuLeftBlock> = isSsku
    ? {
        item_type: MegaMenuBlockTypes.LeftBlock,
        external_link: null,
        text_external_link: null,
        type_left_block: MegaMenuLeftBlockItemsTypes.BlockItem,
        title: t(`header.skills`),
        items: [
          {
            title: t('header.digital'),
            sub_title: t('header.stayAhead'),
            link: buildDiscoverRoute('digital'),
          },
          {
            title: t('header.leadership'),
            sub_title: t('header.excelWithInsights'),
            link: buildDiscoverRoute('leadership'),
          },
          {
            title: t('header.netZero'),
            sub_title: t('header.masterSustainability'),
            link: buildDiscoverRoute('net-zero'),
          },
        ],
      }
    : baseCategoriesList.length
    ? {
        item_type: MegaMenuBlockTypes.LeftBlock,
        external_link: DISCOVER_SEARCH_ROUTE,
        type_left_block: MegaMenuLeftBlockItemsTypes.ListItem,
        title: t(`header.learnerProfiles`),
        text_external_link: t(`header.exploreAll`),
        items: baseCategoriesList?.map((item) => ({
          title: item?.title,
          link: buildDiscoverRoute(item?.slug),
        })),
      }
    : null;

  const middleBlockItems = (isSsku ? createMiddleItemsSsku : createMiddleItems)(
    contentAvailability,
  );

  const middleBLock: Maybe<MegaMenuMiddleBlock> =
    middleBlockItems.length > 1
      ? {
          item_type: MegaMenuBlockTypes.MiddleBlock,
          title: t(`header.exploreOurLearningPathways`),
          clickable_items: middleBlockItems.map((item) => ({
            ...item,
            title: t(`header.${item.title}`),
            sub_title: t(`header.${item.sub_title}`),
            list_items: [],
          })),
        }
      : null;

  const rightBlock: Maybe<MegaMenuRightBlock> = twoLatestVideos.length
    ? {
        item_type: MegaMenuBlockTypes.RightBlock,
        title: t(`header.trendingCourses`),
        items: twoLatestVideos.map((item) => ({
          title: item?.title,
          description: item?.description,
          image_url: item?.preview_url,
          link: `/videos/${item?.video_url}`,
          play_icon: true,
        })),
      }
    : null;

  return [leftBlock, middleBLock, rightBlock].filter(toBoolean<NavigationMegaMenuBlock>);
};

export const getHeaderActions = (navigationItems) => {
  return {
    buttonData: navigationItems?.find((item) => item.type === 'Button'),
    filledButtonData: navigationItems?.find((item) => item.type === 'FilledButton'),
    userData: navigationItems?.find((item) => item.type === 'User'),
    loginButton: navigationItems?.find((item) => item.type === 'LoginButton'),
  };
};

export const getButtonProps = (url, location, loginWithPopup) => {
  if (url === LOGIN_ROUTE) {
    if (checkIsIframed()) {
      return {
        to: LOGIN_ROUTE,
        onClick: (e) => {
          e.preventDefault();
          loginWithPopup();
        },
      };
    } else {
      return {
        to: 'login',
        state: { prevPath: location.pathname, prevSearch: location.search },
      };
    }
  } else {
    return {
      to: url,
    };
  }
};
